import BaseApi from '@/core/api/baseApi'
import type { Order, PaginatedResponse } from '@/core/types/api'

export interface UpdateOrderStatusData {
  status: string
  notes?: string
}

export interface UpdateOrderBillingData {
  company?: string
  cin?: string
  tin?: string
  name?: string
  phone?: string
  address?: string
  city?: string
  postalCode?: string
  country?: string
}

export default class OrdersApi extends BaseApi {
  async getOrders(
    projectCode: string,
    params: Record<string, string | undefined> = {},
    page: number = 1,
    limit: number = 20
  ): Promise<PaginatedResponse<Order>> {
    return this.get<PaginatedResponse<Order>>(`/api-admin/${projectCode}/orders`, {
      params: {
        page: page.toString(),
        limit: limit.toString(),
        ...params
      }
    })
  }

  async getOrder(id: string, projectCode: string): Promise<Order> {
    return this.get<Order>(`/api-admin/${projectCode}/orders/${id}`)
  }

  async getCustomerOrders(
    customerId: string,
    projectCode: string,
    page: number = 1,
    limit: number = 20
  ): Promise<PaginatedResponse<Order>> {
    return this.get<PaginatedResponse<Order>>(`/api-admin/${projectCode}/orders`, {
      params: {
        customer: customerId,
        page: page.toString(),
        limit: limit.toString()
      }
    })
  }

  async updateOrderStatus(
    id: string,
    statusData: UpdateOrderStatusData,
    projectCode: string
  ): Promise<Order> {
    return this.patch<Order, UpdateOrderStatusData>(
      `/api-admin/${projectCode}/orders/${id}/status`,
      statusData
    )
  }

  async resendPaymentInformation(id: string, projectCode: string): Promise<{ message: string }> {
    return this.post<{ message: string }>(`/api-admin/${projectCode}/orders/${id}/resend-payment`)
  }

  async updateOrderBilling(
    id: string,
    billingData: UpdateOrderBillingData,
    projectCode: string
  ): Promise<Order> {
    return this.patch<Order, UpdateOrderBillingData>(
      `/api-admin/${projectCode}/orders/${id}/billing`,
      billingData
    )
  }

  async getOrdersWithUnreadNotes(
    projectCode: string
  ): Promise<{ count: number; orders: Order[] }> {
    return this.get<{ count: number; orders: Order[] }>(
      `/api-admin/${projectCode}/orders/unread-notes`
    )
  }

  async markNotesAsRead(id: string, projectCode: string): Promise<Order> {
    return this.patch<Order, Record<string, never>>(
      `/api-admin/${projectCode}/orders/${id}/mark-notes-read`,
      {}
    )
  }
}

export const ordersApi = BaseApi.createApiInstance(OrdersApi)()
